/** @format */
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';

import { Link } from '@toucan/client/shared/components/link';

import { PaginationVariant } from '../search-results-display/constants';
import { StyledPaginationControls } from './pagination-controls.styles';

export function PaginationControls({
    paginationVariant,
    displayAllResults,
    isLoading,
    totalResultsCount,
    resultsInViewCount,
    onSeeAllClick,
    className,
}: {
    paginationVariant: PaginationVariant;
    displayAllResults: boolean;
    isLoading: boolean;
    totalResultsCount: number;
    resultsInViewCount: number;
    onSeeAllClick: () => void;
    className?: string;
}) {
    const { t } = useTranslation();

    const isPageSelectVariant =
        paginationVariant === PaginationVariant.PAGE_SELECT;
    const isMobile = useMediaQuery(MediaQuery.MOBILE);
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);

    const isBottomPosition = false;

    const shouldRenderTotalResults = isPageSelectVariant // eslint-disable-line no-nested-ternary
        ? isMobile
            ? !isBottomPosition
            : true
        : true;

    const displayShowAllControl =
        !displayAllResults && totalResultsCount > resultsInViewCount;

    function renderTotalResults() {
        if (isLoading || totalResultsCount === -1) {
            return (
                <Skeleton
                    data-testid="search-results__results-skeleton"
                    className="total-results-skeleton"
                    variant={SkeletonVariant.TEXT}
                />
            );
        }

        if (isSmallTablet) {
            return (
                <span data-testid="search-results__mobile-result-count">
                    {t('{{totalResultsCount}} results', {
                        totalResultsCount: totalResultsCount.toString(),
                    })}
                </span>
            );
        }

        return (
            <span data-testid="search-results__desktop-result-count">
                {t(
                    'Showing {{displayedResults}} of {{totalResultsCount}} results',
                    {
                        displayedResults: displayAllResults
                            ? totalResultsCount.toString()
                            : resultsInViewCount.toString(),
                        totalResultsCount: totalResultsCount.toString(),
                    }
                )}
            </span>
        );
    }

    return (
        <StyledPaginationControls className={className}>
            <div className="results-meta" data-testid="search-results__meta">
                {shouldRenderTotalResults && renderTotalResults()}

                {displayShowAllControl && (
                    <Link
                        onClick={onSeeAllClick}
                        data-testid="search-results__see-all"
                    >
                        {t('See all')}
                    </Link>
                )}
            </div>
        </StyledPaginationControls>
    );
}
